/* assets/section-product-compare.js */
(() => {
  'use strict';

  const ROOT_SEL = '.product-compare';
  const instances = new WeakMap();

  function getHeaderOffset() {
    const selectors = [
      '#shopify-section-header',
      '.shopify-section-header-sticky',
      '.section-header',
      'sticky-header',
      'header-drawer',
      '.header-wrapper',
      'header',
    ];

    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (!el) continue;
      const height = el.getBoundingClientRect().height;
      if (height > 0) return height;
    }

    return 0;
  }

  function clearRowHeight(el) {
    if (!el) return;
    el.style.height = '';
    el.style.minHeight = '';
  }

  function setRowHeight(el, height) {
    if (!el) return;
    const value = `${Math.ceil(height)}px`;
    el.style.height = value;
    el.style.minHeight = value;
  }

  class ProductCompare {
    constructor(root) {
      this.root = root;
      this.columns = Array.from(root.querySelectorAll('[data-pc-column]'));
      this.labelRows = Array.from(root.querySelectorAll('[data-pc-label-row]'));
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.equalizeRows = this.equalizeRows.bind(this);
      this.rafId = null;
      this.resizeObserver = null;
      this.equalizeTimer = null;
    }

    init() {
      if (this.root.dataset.pcReady === 'true') return;
      this.root.dataset.pcReady = 'true';

      this.columns.forEach((column) => {
        const trigger = column.querySelector('[data-pc-trigger]');
        const menu = column.querySelector('[data-pc-menu]');
        if (!trigger || !menu) return;

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleSelect(column);
        });

        menu.querySelectorAll('[data-pc-option]').forEach((option) => {
          option.addEventListener('click', (event) => {
            event.preventDefault();
            if (option.classList.contains('is-disabled')) return;
            this.selectProduct(column, option.dataset.productId, option.textContent.trim());
          });
        });
      });

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('load', this.onWindowLoad);

      this.root.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', this.equalizeRows, { once: true });
        }
      });

      this.updateUniqueOptions();
      this.scheduleEqualize();
      this.updateStickySelects();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleEqualize());
        const labels = this.root.querySelector('[data-pc-labels]');
        if (labels) this.resizeObserver.observe(labels);
        this.columns.forEach((column) => {
          const active = column.querySelector('.product-compare__panel.is-active');
          if (active) this.resizeObserver.observe(active);
        });
      }
    }

    destroy() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('load', this.onWindowLoad);
      if (this.equalizeTimer) window.clearTimeout(this.equalizeTimer);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.root.dataset.pcReady = 'false';
      this.closeAllSelects();
    }

    onDocClick(event) {
      if (this.root.contains(event.target) && event.target.closest('[data-pc-select]')) return;
      this.closeAllSelects();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closeAllSelects();
    }

    onResize() {
      this.scheduleEqualize();
      this.updateStickySelects();
    }

    onScroll() {
      this.updateStickySelects();
    }

    onWindowLoad() {
      this.scheduleEqualize();
    }

    scheduleEqualize() {
      if (this.equalizeTimer) window.clearTimeout(this.equalizeTimer);
      this.equalizeTimer = window.setTimeout(() => {
        requestAnimationFrame(() => {
          this.equalizeRows();
          requestAnimationFrame(() => this.equalizeRows());
        });
      }, 16);
    }

    toggleSelect(column) {
      const select = column.querySelector('[data-pc-select]');
      if (!select) return;
      const willOpen = !select.classList.contains('is-open');
      this.closeAllSelects();
      if (willOpen) {
        select.classList.add('is-open');
        const trigger = select.querySelector('[data-pc-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
    }

    closeAllSelects() {
      this.root.querySelectorAll('[data-pc-select].is-open').forEach((select) => {
        select.classList.remove('is-open');
        const trigger = select.querySelector('[data-pc-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    selectProduct(column, productId, title) {
      if (!productId) return;

      column.querySelectorAll('[data-pc-panel]').forEach((panel) => {
        const active = panel.dataset.productId === String(productId);
        panel.classList.toggle('is-active', active);
        if (active) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });

      const triggerText = column.querySelector('[data-pc-trigger-text]');
      if (triggerText && title) triggerText.textContent = title;

      column.querySelectorAll('[data-pc-option]').forEach((option) => {
        option.setAttribute(
          'aria-selected',
          option.dataset.productId === String(productId) ? 'true' : 'false'
        );
      });

      column.dataset.activeProductId = String(productId);
      this.closeAllSelects();
      this.updateUniqueOptions();
      this.scheduleEqualize();

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        const labels = this.root.querySelector('[data-pc-labels]');
        if (labels) this.resizeObserver.observe(labels);
        this.columns.forEach((col) => {
          const active = col.querySelector('.product-compare__panel.is-active');
          if (active) this.resizeObserver.observe(active);
        });
      }
    }

    updateUniqueOptions() {
      const selectedIds = this.columns
        .map((column) => column.dataset.activeProductId)
        .filter(Boolean);

      this.columns.forEach((column) => {
        const currentId = column.dataset.activeProductId;
        column.querySelectorAll('[data-pc-option]').forEach((option) => {
          const optionId = option.dataset.productId;
          const disabled = selectedIds.includes(optionId) && optionId !== currentId;
          option.classList.toggle('is-disabled', disabled);
          option.setAttribute('aria-disabled', disabled ? 'true' : 'false');
          option.tabIndex = disabled ? -1 : 0;
        });
      });
    }

    getActiveSpecRows(column) {
      const active = column.querySelector('.product-compare__panel.is-active');
      if (!active) return [];
      return Array.from(active.querySelectorAll('[data-pc-spec-row]'));
    }

    equalizeGroup(elements) {
      const cells = elements.filter(Boolean);
      if (!cells.length) return;

      cells.forEach(clearRowHeight);

      let maxHeight = 0;
      cells.forEach((cell) => {
        maxHeight = Math.max(maxHeight, cell.getBoundingClientRect().height);
      });

      cells.forEach((cell) => setRowHeight(cell, maxHeight));
    }

    equalizeRows() {
      // 1) Select / choose-product row
      const selectSlots = [
        this.root.querySelector('[data-pc-labels] [data-pc-select-slot]'),
        ...this.columns.map((column) => column.querySelector('[data-pc-select-slot]')),
      ];
      this.equalizeGroup(selectSlots);

      // 2) Product / media row
      const mediaCells = [
        this.root.querySelector('[data-pc-label-media]'),
        ...this.columns.map((column) => {
          const active = column.querySelector('.product-compare__panel.is-active');
          return active ? active.querySelector('[data-pc-media]') : null;
        }),
      ];
      this.equalizeGroup(mediaCells);

      // 3) Spec rows (Price + custom specs)
      const rowCount = this.labelRows.length;
      for (let index = 0; index < rowCount; index += 1) {
        const cells = [
          this.labelRows[index],
          ...this.columns.map((column) => this.getActiveSpecRows(column)[index] || null),
        ];
        this.equalizeGroup(cells);
      }
    }

    updateStickySelects() {
      const table = this.root.querySelector('[data-pc-table]');
      if (!table) return;

      const headerOffset = getHeaderOffset();
      const tableRect = table.getBoundingClientRect();

      this.columns.forEach((column) => {
        const slot = column.querySelector('[data-pc-select-slot]');
        const select = column.querySelector('[data-pc-select]');
        if (!slot || !select) return;

        const slotRect = slot.getBoundingClientRect();
        const shouldStick =
          slotRect.top + 120 <= headerOffset &&
          tableRect.bottom - slotRect.height > headerOffset + 40;

        if (shouldStick) {
          slot.classList.add('is-top-fixed');
          slot.style.setProperty('--pc-sticky-top', `${headerOffset}px`);
          slot.style.setProperty('--pc-sticky-left', `${slotRect.left}px`);
          slot.style.setProperty('--pc-sticky-width', `${slotRect.width}px`);
          slot.style.minHeight = `${select.getBoundingClientRect().height + 12}px`;
        } else {
          slot.classList.remove('is-top-fixed');
          slot.style.removeProperty('--pc-sticky-top');
          slot.style.removeProperty('--pc-sticky-left');
          slot.style.removeProperty('--pc-sticky-width');
        }
      });
    }
  }

  function initRoot(root) {
    if (!root) return;
    const existing = instances.get(root);
    if (existing) existing.destroy();
    const instance = new ProductCompare(root);
    instance.init();
    instances.set(root, instance);
  }

  function destroyRoot(root) {
    const existing = instances.get(root);
    if (!existing) return;
    existing.destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SEL).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SEL).forEach(destroyRoot);
  });
})();
